import { AuthenticatedHttpClient } from "../utility/AuthenticatedHttpClient";
import { HttpClient } from "../utility/HttpClient";
import { Api, IApi } from "../utility/IApi";
import { AuthenticationService, IAuthenticationService } from "../utility/IAuthenticationService";
import { IConfig } from "../IConfig";
import { Observable } from "rxjs";
import { IHttpClient } from "../utility/IHttpClient";
import { IDataStore, DatabaseKey } from "../repository/IDataStore";
import { IAdminServicesProvider, AdminServicesProvider } from "./IAdminServicesProvider";
import { IUser, IOrganisation } from "../model/ModelTypes";
import { isNullOrUndefined } from "util";
import { IInputValidator, InputValidator } from "../utility/IInputValidator";
import { IUserProfileOperations } from "../utility/public/IUserProfileOperations";
import { UserProfileOperations } from "../utility/public/UserProfileOperations";
import { flatMap, map, first } from "rxjs/operators";

/**
 * Defines Interface for avialable non-admin which can be provided
 * by an instance of IServicesProvider
 */
export interface IServicesProvider {
    getApi(): IApi;
    getConfig(): IConfig;
    getAuthenticationService(): IAuthenticationService;
    getDataStore(): IDataStore;
    getAuthenticatedHttpClient(): IHttpClient;
    getHttpClient(): IHttpClient;
    getAdminServicesProvider(): IAdminServicesProvider;
    getUser(): IUser;
    getInputValidator(): IInputValidator;
    getUserProfileOperations(): Observable<IUserProfileOperations>;
    getOrganisation(): IOrganisation;
}
export class ServicesProvider implements IServicesProvider {
    private authenticatedHttpClient: IHttpClient;
    private httpClient: IHttpClient;
    private dataStore: IDataStore;
    private api: Api;
    private authenticationService: AuthenticationService;
    private config: IConfig;
    private user: IUser;
    private organisation: IOrganisation;
    private adminProvider: IAdminServicesProvider;

    /**
     * Prepares and returns an observable to get an instance of IServicesProvider
     * @param config Environment Configuration for the web app
     * @param dataStore IDataStore instance to read from IndexedDB
     * @returns Observable<IServicesProvider>
     */
    public static GetServicesProvider(config: IConfig, dataStore: IDataStore): Observable<IServicesProvider> {
        let servicesProvider = new ServicesProvider();
        servicesProvider.config = config;
        servicesProvider.dataStore = dataStore;
        return dataStore.getDocuments<Observable<IUser>>(DatabaseKey.user)
        .pipe(
            flatMap(item => item),
            flatMap( item => {
                servicesProvider.user = item;
                return AuthenticatedHttpClient.GetClient(servicesProvider.dataStore, servicesProvider.config);
            }),
            flatMap( item => {
                servicesProvider.authenticatedHttpClient = item;
                servicesProvider.httpClient = new HttpClient(servicesProvider.config);
                servicesProvider.api = new Api(servicesProvider.httpClient,
                                               servicesProvider.authenticatedHttpClient);  
                servicesProvider.authenticationService = new AuthenticationService(servicesProvider.api,
                                                                                   servicesProvider.dataStore);   
                return dataStore.getDocuments<Observable<IOrganisation>>(
                    DatabaseKey.organisation
                );                                                                               
            }),
            flatMap(item => item.first()),
            flatMap(
                item => {
                    if (isNullOrUndefined(item)) {
                        let organisation: IOrganisation;
                        return servicesProvider.api.getOrganisationDetails(servicesProvider.user.organisation)
                        .pipe(
                            flatMap(org => {
                                organisation = org;
                                return dataStore.insertDocument<IOrganisation>(org, DatabaseKey.organisation);
                            }),
                            map(res => organisation)
                        );
                    } else {
                        return Observable.of(item);
                    }
                }
            ),
            map(result => {
                servicesProvider.organisation = result;
                return servicesProvider;
            }),
            flatMap(preparedServicesProvider => {
                if (servicesProvider.user.is_admin) {
                    return AdminServicesProvider.Build(
                        servicesProvider,
                        servicesProvider.user.organisation
                    ).pipe(
                        first(),
                        map(admin => {
                            servicesProvider.adminProvider = admin;
                            return servicesProvider;
                        })
                    );
                } else {
                    Observable.of(servicesProvider);
                }
            })
        ).first();
    }
    /**
     * Prepares and returns an observable to get an instance of IServicesProvider
     * @param config Environment Configuration for the web app
     * @param dataStore IDataStore instance to read from IndexedDB
     * @param user User instance
     * @returns Observable<IServicesProvider>
     */
    public static GetServicesProviderWithUser(config: IConfig, dataStore: IDataStore, user: IUser):
    Observable<IServicesProvider> {
        let servicesProvider = new ServicesProvider();
        servicesProvider.config = config;
        servicesProvider.dataStore = dataStore;
        servicesProvider.user = user;
        return AuthenticatedHttpClient.GetClient(servicesProvider.dataStore, servicesProvider.config)
        .pipe(
            flatMap(item => {
                servicesProvider.authenticatedHttpClient = item;
                servicesProvider.httpClient = new HttpClient(servicesProvider.config);
                servicesProvider.api = new Api(servicesProvider.httpClient,
                                               servicesProvider.authenticatedHttpClient);  
                servicesProvider.authenticationService = new AuthenticationService(servicesProvider.api,
                                                                                   servicesProvider.dataStore);  
                return  dataStore.getDocuments<Observable<IOrganisation>>(DatabaseKey.organisation);
            }),
            flatMap(item => {
                return item.first();
            }),
            flatMap(
                item => {
                    if (isNullOrUndefined(item)) {
                        return servicesProvider.api.getOrganisationDetails(user.organisation)
                        .pipe(
                            flatMap(organisation => {
                                return dataStore
                                .insertDocument<IOrganisation>(organisation, DatabaseKey.organisation)
                                .pipe(
                                    map(result => organisation)
                                );
                            }),
                            map(organisation => organisation)
                        );
                    } else {
                        return Observable.of(item);
                    }
                }
            ),
            map( item => {
                servicesProvider.organisation = item;
                return servicesProvider;                                                                                
            }),
            flatMap(provider => {
                if (provider.user.is_admin) {
                    return AdminServicesProvider.Build(
                        provider,
                        provider.user.organisation
                    ).pipe(
                        first(),
                        map(admin => {
                            provider.adminProvider = admin;
                            return provider;
                        })
                    );
                } else {
                    return Observable.of(provider);
                }
                
            })
        );
    }
    getOrganisation(): IOrganisation {
        return this.organisation;
    }
    getApi(): IApi {
        return this.api;
    }
    getConfig(): IConfig {
        return this.config;
    }
    getAuthenticationService(): IAuthenticationService {
        return this.authenticationService;
    }
    getDataStore(): IDataStore {
        return this.dataStore;
    }
    getAuthenticatedHttpClient(): IHttpClient {
        return this.authenticatedHttpClient;
    }
    getHttpClient(): IHttpClient {
        return this.httpClient;
    }
    getUserProfileOperations(): Observable<IUserProfileOperations> {
        return UserProfileOperations.GetOperations(this.dataStore, this.api);
    }
    getAdminServicesProvider(): IAdminServicesProvider {
        if (this.user.is_admin) {
            if (!isNullOrUndefined(this.user.organisation)) {
                return this.adminProvider;
            } else {
                throw new Error("Invalid User Details");
            }
        } else {
            throw new Error("User is not an admin");
        }
    }
    getUser(): IUser {
        return this.user;
    }
    getInputValidator(): IInputValidator {
        return new InputValidator();
    }
    private constructor() {

    }

}


// WEBPACK FOOTER //
// ./src/di/IServicesProvider.ts